/* eslint-disable @typescript-eslint/no-explicit-any */
import clsx from 'clsx';
import 'quill/dist/quill.snow.css';

import Quill from 'quill';
import React, { useRef, useState, useEffect } from 'react';

import './textEditor.css';

import { EDITOR_FONT_OPTIONS } from '@/constants/constant';
import { useSettingsStore } from '@/store/useSettingsStore';

interface ITextEditorProps {
  label?: string;
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  error?: string;
  isRequired?: boolean;
  wrapperClassName?: string;
  disabled?: boolean;
  maxLength?: number;
  isFullToolbar?: boolean;
  showCount?: boolean;
}

const Font = Quill.import('formats/font') as any;
Font.whitelist = EDITOR_FONT_OPTIONS.map((f) => f.value);
Quill.register(Font, true);

const TextEditor: React.FC<ITextEditorProps> = ({
  label,
  value = '',
  onChange = () => {},
  error,
  isRequired,
  wrapperClassName,
  disabled,
  maxLength,
  isFullToolbar = false,
  placeholder,
  showCount = false,
}) => {
  const quillRef = useRef<Quill | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const _isUpdatingRef = useRef(false);
  const _lastValueRef = useRef(value);
  const { direction } = useSettingsStore((state) => state.settings);
  const [currentLength, setCurrentLength] = useState(value?.length || 0);

  const getToolbarOptions = () => {
    const basicOptions = [
      [
        'bold',
        'italic',
        'underline',
        'link',
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ], // basic formatting
    ];

    const fullOptions = [
      [{ font: Font.whitelist }, { header: [1, 2, 3, false] }],
      [
        'bold',
        'italic',
        'underline',
        'strike',
        'link',
        'image',
        { color: [] },
        { background: [] },
        { align: [] },
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
        { direction: 'rtl' },
        'clean',
      ], // toggled buttons
    ];

    return isFullToolbar ? fullOptions : basicOptions;
  };

  const TOOLBAR_OPTIONS = getToolbarOptions();

  const normalizeQuillLists = (html: string): string => {
    if (!html || !html.includes('data-list')) return html;

    return html
      .replace(
        /<ol>(\s*(?:<li\s+data-list="bullet"[^>]*>[\s\S]*?<\/li>\s*)+)<\/ol>/gi,
        (_, items) => `<ul>${items.replace(/\s*data-list="bullet"/g, '')}</ul>`
      )
      .replace(/\s*data-list="ordered"/g, '');
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // If click is inside a dropdown picker, do nothing
      if (target.closest('.ql-picker')) return;

      // Otherwise close all dropdowns
      const expandedPickers = document.querySelectorAll('.ql-expanded');
      expandedPickers.forEach((picker) => {
        picker.classList.remove('ql-expanded');
      });
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  useEffect(() => {
    const container = editorRef.current;
    if (!container) return;

    if (quillRef.current) {
      quillRef.current.off('text-change');
      quillRef.current = null;
    }

    container.innerHTML = '';

    const editorElement = document.createElement('div');
    editorElement.setAttribute('role', 'textbox');
    editorElement.setAttribute('aria-multiline', 'true');
    editorElement.setAttribute('aria-label', label || 'Text editor');

    container.appendChild(editorElement);

    const quill = new Quill(editorElement, {
      modules: {
        toolbar: TOOLBAR_OPTIONS,
        clipboard: {
          matchVisual: false,
        },
      },
      theme: 'snow',
      placeholder: placeholder || '',
      formats: [
        'font', // font family
        'size', // optional if you add size picker
        'header', // header 1-6
        'align', // text alignment
        'bold',
        'italic',
        'underline',
        'strike',
        'list', // ordered, bullet, check
        'indent', // indent +1/-1
        'direction', // rtl / ltr
        'color', // font color
        'background', // highlight
        'link',
        'image',
      ],
    });

    quillRef.current = quill;

    requestAnimationFrame(() => {
      quill.blur();
    });

    //for custom font handling to preserve font family
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (node instanceof HTMLElement) {
        const classList = Array.from(node.classList);
        const fontClass = classList.find((cls) => cls.startsWith('ql-font-'));

        if (fontClass) {
          const fontValue = fontClass.replace('ql-font-', '');

          delta.ops = delta.ops?.map((op: any) => ({
            ...op,
            attributes: {
              ...op.attributes,
              font: fontValue,
            },
          }));
        }
      }

      return delta;
    });

    // Sync font dropdown with cursor position
    const handleSelectionChange = (range: any) => {
      if (!range) return;

      const format = quill.getFormat(range) as any;
      const fontValue = format.font;

      const picker = container.querySelector(
        '.ql-font .ql-picker-label'
      ) as HTMLElement;
      if (!picker) return;

      // Detect if current line is empty
      const [line] = quill.getLine(range.index);
      const lineText = line?.domNode?.innerText?.trim();

      if (!fontValue || !lineText) {
        // Reset to default
        picker.removeAttribute('data-value');

        // Also remove inline font format if line is empty
        quill.format('font', false);
      } else {
        picker.setAttribute('data-value', fontValue);
      }
    };

    quill.on('selection-change', handleSelectionChange);

    // Handle link tooltip positioning
    const adjustTooltipPosition = () => {
      const tooltip = container.querySelector('.ql-tooltip') as HTMLElement;
      if (!tooltip) return;

      const { left, right } = tooltip.getBoundingClientRect();
      const { left: containerLeft, right: containerRight } =
        container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Check if tooltip overflows on the left side of container or viewport
      if (left < containerLeft + 10 || left < 10) {
        tooltip.style.left = '10px';
        tooltip.style.right = 'auto';
      }
      // Check if tooltip overflows on the right side of container or viewport
      else if (right > containerRight - 10 || right > viewportWidth - 10) {
        tooltip.style.left = 'auto';
        tooltip.style.right = '10px';
      }
    };

    // Use MutationObserver to detect when tooltip appears
    const observer = new MutationObserver(() => {
      adjustTooltipPosition();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
      _lastValueRef.current = value;
    }

    const handleTextChange = () => {
      if (_isUpdatingRef.current) return;

      let html = quill.root.innerHTML;
      if (html === '<p><br></p>') {
        html = '';
      }

      const normalizedHtml = normalizeQuillLists(html);

      const text = quill.getText();
      const length = text.endsWith('\n') ? text.length - 1 : text.length;

      if (showCount) {
        setCurrentLength(length);
      }

      if (maxLength && length > maxLength) {
        quill.deleteText(maxLength, length);
        return;
      }

      _lastValueRef.current = normalizedHtml;
      onChange(normalizedHtml);
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', handleSelectionChange);
      observer.disconnect();

      quillRef.current = null;
      container.innerHTML = '';
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!disabled);
    }
  }, [disabled]);

  useEffect(() => {
    const editor = quillRef.current;
    if (!editor) return;

    if (_lastValueRef.current === value) return;
    _lastValueRef.current = value;

    const currentHtml = editor.root.innerHTML || '';

    const normalizeHtml = (html: string) => {
      return html.replace(/<p><br><\/p>$/, '').trim();
    };

    const normalizedCurrent = normalizeHtml(currentHtml);
    const normalizedValue = normalizeHtml(value || '');

    if (value && normalizedValue !== normalizedCurrent) {
      const selection = editor.getSelection();

      _isUpdatingRef.current = true;

      if (editor.root.innerHTML !== value) {
        editor.clipboard.dangerouslyPasteHTML(value);
      }

      if (selection && selection.index >= 0) {
        const newLength = editor.getLength() - 1;
        const safeIndex = Math.min(selection.index, newLength);
        const safeLength = Math.min(selection.length, newLength - safeIndex);

        setTimeout(() => {
          try {
            editor.setSelection(safeIndex, safeLength);
          } catch {
            editor.setSelection(newLength, 0);
          }
        }, 0);
      }

      setTimeout(() => {
        _isUpdatingRef.current = false;
      }, 10);
    }

    if (!value && currentHtml && currentHtml !== '<p><br></p>') {
      _isUpdatingRef.current = true;
      editor.setText('');
      setTimeout(() => {
        _isUpdatingRef.current = false;
      }, 0);
    }
  }, [value]);

  const handleContainerClick = (e: React.MouseEvent) => {
    if (!disabled && quillRef.current) {
      const target = e.target as HTMLElement;
      if (!target.closest('.ql-toolbar')) {
        quillRef.current.focus();
        const selection = quillRef.current.getSelection(true);
        if (!selection) {
          const length = quillRef.current.getLength();
          quillRef.current.setSelection(length - 1, 0);
        }
      }
    }
  };

  return (
    <div
      className={clsx(
        'flex flex-col justify-center items-start gap-1.5 w-full rounded-xl',
        wrapperClassName
      )}
    >
      {/* Label */}
      {label && (
        <label className="text-neutral-900 text-sm font-medium">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Editor Container */}
      <div
        className={clsx(
          'w-full rounded-lg flex flex-col relative cursor-text text-editor-container group shadow-s transition-all duration-200',
          disabled
            ? 'border border-neutral-light-200 bg-neutral-100! cursor-not-allowed! focus-within:outline-none'
            : error
              ? 'border border-error-default focus-within:outline-none bg-white'
              : 'border border-neutral-light-200 focus-within:outline-2 bg-white focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff]'
        )}
        onClick={handleContainerClick}
        role="group"
        aria-labelledby={label ? `editor-label-${label}` : undefined}
      >
        {/* Editor */}
        <div
          ref={editorRef}
          className={clsx(
            'h-auto! flex-1 overflow-hidden border-none! outline-none!',
            disabled && 'cursor-not-allowed!',
            direction === 'rtl' && 'text-right'
          )}
          dir={direction}
          aria-disabled={disabled}
          tabIndex={-1}
        />
        {showCount && maxLength && (
          <div className="flex justify-end px-3 pb-2">
            <span className="text-[10px] text-neutral-disabled">
              {currentLength}/{maxLength}
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm font-normal text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

export default TextEditor;
